import React, { useCallback, useRef } from "react";
import MarkerCluster from "@googlemaps/markerclustererplus";
import styled from "@emotion/styled";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import { IconButton } from "@material-ui/core";
import { VehicleRouteInfo } from "../../Home/assets/ClosedLoopRoutes";
import { SPACING_8, SPACING_36 } from "../../common/styles/spacing";
import { BLACK, WHITE } from "../../common/styles/colors";
import { BOX_SHADOW_1 } from "../../common/styles/box-shadow";
import { useGoogleMaps } from "../hooks/useGoogleMaps";
import { useStationaryMarkers, MapMarker, ClusterDetails } from "../hooks/useStationaryMarkers";
import { useMovingMarkers, MovingMapMarker, MovingMarkerRef } from "../hooks/useMovingMarkers";
import { PolyLineObject, usePolyline } from "../hooks/usePolyLine";

export interface MapProps {
  options?: google.maps.MapOptions;
  center?: google.maps.LatLngLiteral;
  markers?: MapMarker[];
  markersClusterDetails?: ClusterDetails;
  movingMarkers?: MovingMapMarker[];
  movingMarkerClusterDetails?: ClusterDetails;
  staticRouteInfo?: VehicleRouteInfo[];
  polyLines?: PolyLineObject[];
}

const MapWrapper = styled.div`
    height: 100%;
    width: 100%;
`;

const MapContainer = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
`;

const ResetButton = styled(IconButton)`
    position: absolute;

    padding: ${SPACING_8};

    bottom: ${SPACING_36};
    left: ${SPACING_8};

    border-radius: 0;
    background: ${WHITE};
    
    box-shadow: ${BOX_SHADOW_1};

    &:hover {
        background-color: ${WHITE};

        & svg {
            fill: ${BLACK};
        }
    }

    & svg {
        transform: scale(1.25);
    }
`;

/**
 * Note the reason we are using ref vs state is
 * because this is our wrapper on top of the Maps Library
 * There are no actual dom elements being updated so this is
 * actually a more performant way of updating the component
 * */
export default ({
    options,
    center,
    markers = [],
    movingMarkers = [],
    markersClusterDetails,
    movingMarkerClusterDetails,
    staticRouteInfo,
    polyLines,
}: MapProps) => {
    // The HTML Div that google maps hooks into
    const mapRef = useRef<HTMLDivElement>(null);
    // Instance of google maps
    const googleMapRef = useRef<google.maps.Map>(null);

    // Ref to store the static, non moving markers
    const markersRef = useRef<google.maps.Marker[]>([]);

    // Ref to store the moving markers (ie vehicles) on the map
    const movingMarkerRef = useRef<MovingMarkerRef>({});

    // Ref to store the info windows associated with moving markers
    const movingMarkerInfoWindowRef = useRef<{
        [keys: string]: google.maps.InfoWindow;
    }>({});

    // The cluster instance for the static markers
    const markerClusterRef = useRef<MarkerCluster>(null);

    // The cluster instance for the moving markers
    const movingMarkerClusterRef = useRef<MarkerCluster>(null);

    const polylinesRef = useRef<{ [keys: string]: google.maps.Polyline }>({});

    const cleanupRefs = () => {
        markersRef.current.forEach((m) => m.setMap(null));
        Object.values(movingMarkerRef.current).forEach((m) => m.setMap(null));
        markersRef.current = [];
        movingMarkerRef.current = {};
        movingMarkerInfoWindowRef.current = {};
        markerClusterRef.current?.clearMarkers();
        (markerClusterRef.current as unknown) = null;
        movingMarkerClusterRef.current?.clearMarkers();
        (movingMarkerClusterRef.current as unknown) = null;
        Object.values(polylinesRef.current).forEach((p) => p.setMap(null));
        polylinesRef.current = {};
    };

    useGoogleMaps(
        mapRef,
        googleMapRef,
        cleanupRefs,
        options,
        staticRouteInfo,
        center,
    );

    useStationaryMarkers(
        googleMapRef,
        markersRef,
        markerClusterRef,
        markers,
        markersClusterDetails,
    );

    useMovingMarkers(
        googleMapRef,
        movingMarkers,
        movingMarkerRef,
        movingMarkerInfoWindowRef,
        movingMarkerClusterRef,
        movingMarkerClusterDetails,
        options,
    );

    usePolyline(
        googleMapRef,
        polylinesRef,
        polyLines,
    );

    // Resets the map back to the initial center and zoom of the site
    const resetButtonHandler = useCallback(() => {
        if (googleMapRef.current && options && options.center && options.zoom) {
            googleMapRef.current?.setCenter(options.center);
            googleMapRef.current?.setZoom(options.zoom);
        }
    }, [options]);

    return (
        <MapContainer>
            <MapWrapper id="map" ref={mapRef} />
            <ResetButton onClick={resetButtonHandler}>
                <ZoomOutMapIcon />
            </ResetButton>
        </MapContainer>
    );
};
